import { match, P } from 'ts-pattern'
import { z, ZodIssueCode, ZodParsedType, type ZodErrorMap } from 'zod'

const invalidStringValidations: Record<string, string> = {
    email: 'E-mail inválido',
    url: 'URL inválida',
    uuid: 'UUID inválido',
    regex: 'Formato inválido',
    datetime: 'Data/hora inválida',
    cuid: 'CUID inválido',
}

const ptErrorMap: ZodErrorMap = (issue, ctx) =>
    match(issue)
        .returnType<{ message: string }>()
        .with(
            {
                code: ZodIssueCode.invalid_type,
                received: P.union(ZodParsedType.undefined, ZodParsedType.null),
            },
            () => ({ message: 'Campo obrigatório' }),
        )
        .with({ code: ZodIssueCode.invalid_type }, () => ({
            message: 'Tipo inválido',
        }))
        .with(
            { code: ZodIssueCode.too_small, type: 'string', exact: true },
            issue => ({
                message: `O campo deve conter exatamente ${issue.minimum} caracter(es)`,
            }),
        )
        .with(
            {
                code: ZodIssueCode.too_small,
                type: 'string',
                inclusive: true,
            },
            issue => ({
                message: `O campo deve conter pelo menos ${issue.minimum} caracter(es)`,
            }),
        )
        .with({ code: ZodIssueCode.too_small, type: 'string' }, issue => ({
            message: `O campo deve conter mais de ${issue.minimum} caracter(es)`,
        }))
        .with(
            { code: ZodIssueCode.too_small, type: 'number', exact: true },
            issue => ({
                message: `O valor deve ser exatamente ${issue.minimum}`,
            }),
        )
        .with(
            {
                code: ZodIssueCode.too_small,
                type: 'number',
                inclusive: true,
            },
            issue => ({
                message: `O valor deve ser maior ou igual a ${issue.minimum}`,
            }),
        )
        .with({ code: ZodIssueCode.too_small, type: 'number' }, issue => ({
            message: `O valor deve ser maior que ${issue.minimum}`,
        }))
        .with(
            { code: ZodIssueCode.too_small, type: 'array', exact: true },
            issue => ({
                message: `A lista deve conter exatamente ${issue.minimum} elemento(s)`,
            }),
        )
        .with(
            {
                code: ZodIssueCode.too_small,
                type: 'array',
                inclusive: true,
            },
            issue => ({
                message: `A lista deve conter pelo menos ${issue.minimum} elemento(s)`,
            }),
        )
        .with({ code: ZodIssueCode.too_small, type: 'array' }, issue => ({
            message: `A lista deve conter mais de ${issue.minimum} elemento(s)`,
        }))
        .with({ code: ZodIssueCode.too_small }, () => ({
            message: ctx.defaultError,
        }))
        .with(
            { code: ZodIssueCode.too_big, type: 'string', exact: true },
            issue => ({
                message: `O campo deve conter exatamente ${issue.maximum} caracter(es)`,
            }),
        )
        .with(
            {
                code: ZodIssueCode.too_big,
                type: 'string',
                inclusive: true,
            },
            issue => ({
                message: `O campo deve conter no máximo ${issue.maximum} caracter(es)`,
            }),
        )
        .with({ code: ZodIssueCode.too_big, type: 'string' }, issue => ({
            message: `O campo deve conter menos de ${issue.maximum} caracter(es)`,
        }))
        .with(
            { code: ZodIssueCode.too_big, type: 'number', exact: true },
            issue => ({
                message: `O valor deve ser exatamente ${issue.maximum}`,
            }),
        )
        .with(
            {
                code: ZodIssueCode.too_big,
                type: 'number',
                inclusive: true,
            },
            issue => ({
                message: `O valor deve ser menor ou igual a ${issue.maximum}`,
            }),
        )
        .with({ code: ZodIssueCode.too_big, type: 'number' }, issue => ({
            message: `O valor deve ser menor que ${issue.maximum}`,
        }))
        .with({ code: ZodIssueCode.too_big }, () => ({
            message: ctx.defaultError,
        }))
        .with({ code: ZodIssueCode.invalid_string }, issue => ({
            message:
                typeof issue.validation === 'string'
                    ? (invalidStringValidations[issue.validation] ??
                      'Formato inválido')
                    : 'Formato inválido',
        }))
        .with({ code: ZodIssueCode.invalid_enum_value }, issue => ({
            message: `Valor inválido. Opções: ${issue.options.join(', ')}`,
        }))
        .with({ code: ZodIssueCode.invalid_date }, () => ({
            message: 'Data inválida',
        }))
        .with({ code: ZodIssueCode.invalid_union }, () => ({
            message: 'Entrada inválida',
        }))
        .with({ code: ZodIssueCode.custom }, () => ({
            message: ctx.defaultError,
        }))
        .otherwise(() => ({ message: ctx.defaultError }))

z.setErrorMap(ptErrorMap)

export * from './endpoints/abono'
export * from './endpoints/absenteismo'
export * from './endpoints/adicionais-noturno'
export * from './endpoints/aej'
export * from './endpoints/afastamentos'
export * from './endpoints/afd'
export * from './endpoints/ajuste'
export * from './endpoints/alteracoes'
export * from './endpoints/auditoria'
export * from './endpoints/assinaturas'
export * from './endpoints/avaliacao'
export * from './endpoints/calculo'
export * from './endpoints/certificados'
export * from './endpoints/centro-custo'
export * from './endpoints/centro-custo-funcionario'
export * from './endpoints/cidades'
export * from './endpoints/clientes'
export * from './endpoints/compensacao-manual'
export * from './endpoints/dsr'
export * from './endpoints/empresas'
export * from './endpoints/espelho'
export * from './endpoints/fechamento-abertura-competencia'
export * from './endpoints/feriados'
export * from './endpoints/feriados-cliente'
export * from './endpoints/funcionario'
export * from './endpoints/funcoes'
export * from './endpoints/funcoesAvaliacao'
export * from './endpoints/horas-trabalhadas'
export * from './endpoints/jornada'
export * from './endpoints/locais'
export * from './endpoints/login'
export * from './endpoints/ocorrencia'
export * from './endpoints/pagamentoHoras'
export * from './endpoints/parametros-financeiros'
export * from './endpoints/permissoes'
export * from './endpoints/planos'
export * from './endpoints/registros'
export * from './endpoints/regras-calculo'
export * from './endpoints/relatorio-ponto-diario'
export * from './endpoints/reps'
export * from './endpoints/rubricas'
export * from './endpoints/saldoHoras'
export * from './endpoints/sobreaviso'
export * from './endpoints/supervisor'
export * from './endpoints/tabela-horarios'
export * from './endpoints/troca-horarios'
export * from './endpoints/troca-inicio-ponto'
export * from './endpoints/usuarios'
export * from './endpoints/vale-alimentacao'

export * as Api from '.'

export type ErrorHandler = {
    statusCode: number
    message: string | string[]
    descricao?: string
}
